import { ArrayUtil } from "@mcleod/core";
import { Checkbox } from "../checkbox/Checkbox";
import { CheckboxProps } from "../checkbox/CheckboxProps";
import { CloneComponent } from "../../base/CloneComponent";
import { Component } from "../../base/Component";
import { ComponentPropDefinitions, ComponentProps } from "../../base/ComponentProps";
import { ComponentTypes } from "../../base/ComponentTypes";
import { DataSource } from "../../databinding/DataSource";
import { DataSourceValidationEvent } from "../../events/DataSourceValidationEvent";
import { DesignableObjectLogManager } from "../../logging/DesignableObjectLogManager";
import { EnhancedCompoundComponent } from "../compound/EnhancedCompoundComponent";
import { ExcludableTextboxOrientation } from "./ExcludableTextboxOrientation";
import { ExcludableTextboxPropDefinitions, ExcludableTextboxProps } from "./ExcludableTextboxProps";
import { Event } from "../../events/Event";
import { Label } from "../label/Label";
import { serializeComponents } from "../../serializer/ComponentSerializer";
import { Switch } from "../switch/Switch";
import { SwitchProps } from "../switch/SwitchProps";
import { Textbox } from "../textbox/Textbox";
import { TextboxProps } from "../textbox/TextboxProps";
import { ValidationResult, ValidationResultType } from "../../base/ValidationResult";

const log = DesignableObjectLogManager.getLogger("components.excludabletextbox.ExcludableTextbox");

enum InnerIds {
    CHECKBOX = "checkbox",
    SWITCH_RANGE = "switchRange",
    TEXTBOX = "textbox",
    TEXTBOX_RANGE_START = "textboxRangeStart",
    TEXTBOX_RANGE_END = "textboxRangeEnd"
}

/**
 * An ExcludableTextbox is a panel that can contain the following components:
 * - A 'Range Selection' switch, which indicates if the user is presented a single textbox, or two textboxes that make up a range.
 * - Either a single textbox, or two textboxes that make up a range.
 * - An 'Exclude' checkbox, which indicates if the values entered in the textboxes are to be included or excluded.
 *
 * When the ExcludableTextbox is serialized, it will contain a components array including the following:
 * - A switch component definition.
 * - A single textbox component definition.  This will be used for all 3 possible textboxes that can be presented to the
 *   user (as their important properties will always be the same).
 * - A checkbox component definition for the exclude checkbox.
 */
export class ExcludableTextbox extends EnhancedCompoundComponent implements ExcludableTextboxProps {
    private _checkbox: Checkbox;
    private dashLabel: Label
    private _orientation: ExcludableTextboxOrientation;
    private _rangeDefault: boolean;
    private _rangeOffered: boolean;
    private _switchRange: Switch;
    private _textbox: Textbox;
    private _textboxRangeStart: Textbox;
    private _textboxRangeEnd: Textbox;
    
    constructor(props: Partial<ExcludableTextboxProps>) {
        super(props, false);
        this.setInnerComponentProps(this, { marginTop: 8, padding: 0 });
        this._rangeOffered = false;
        this.setProps({ ...props });
    }

    override syncInnerComponentIds() {
        this.setInnerComponentId(this.checkbox, InnerIds.CHECKBOX);
        this.setInnerComponentId(this.switchRange, InnerIds.SWITCH_RANGE);
        this.setInnerComponentId(this.textbox, InnerIds.TEXTBOX);
        this.setInnerComponentId(this.textboxRangeEnd, InnerIds.TEXTBOX_RANGE_END);
        this.setInnerComponentId(this.textboxRangeStart, InnerIds.TEXTBOX_RANGE_START);
    }

    public get orientation(): ExcludableTextboxOrientation {
        return this._orientation ?? this.getPropertyDefinitions().orientation.defaultValue;
    }

    public set orientation(value: ExcludableTextboxOrientation) {
        if (this.orientation === value)
            return;
        this._orientation = value;
        this.syncOrientation();
    }

    private syncOrientation() {
        const isInline = this.orientation === ExcludableTextboxOrientation.INLINE;
        const switchTextboxRowBreak: Partial<ComponentProps> = {
            rowBreak: !isInline
        };
        const checkboxProps: Partial<CheckboxProps> = {
            marginTop: isInline ? 18 : null,
            marginLeft: isInline ? null : -6,
            rowBreak: isInline
        };
        this.setInnerComponentProps(this.switchRange, switchTextboxRowBreak);
        this.setInnerComponentProps(this.lastTextbox, switchTextboxRowBreak);
        this.setInnerComponentProps(this.checkbox, checkboxProps);

        if (this.switchRange != null) {
            this.switchRange.captionLabelMarginTop = isInline ? -4 : null;
            this.switchRange.captionLabelMarginBottom = isInline ? 8 : null;
        }
    }

    public get checkbox(): Checkbox {
        return this._checkbox;
    }

    private set checkbox(value: Checkbox) {
        if (value == null) {
            log.debug(this, "The 'Exclude' checkbox must remain present");
            return;
        }
        const overrideProps: Partial<CheckboxProps> = {
            id: this.createInnerComponentId(InnerIds.CHECKBOX),
            caption: "Exclude",
            dataSource: this.dataSource,
            field: this.excludeFieldName,
            searchOnly: this.searchOnly,
            enabled: false
        };
        this.setInternalComponent({
            value,
            propName: "_checkbox",
            getPropertyDefinitionsOverride: null,
            defaultProps: null,
            overrideProps
        });
    }

    public get rangeOffered(): boolean {
        return this._rangeOffered ?? this.getPropertyDefinitions().rangeOffered.defaultValue;
    }

    public set rangeOffered(value: boolean) {
        if (value === this._rangeOffered)
            return;
        this._rangeOffered = value;
        this.syncSwitchRange();
    }

    public get rangeDefault(): boolean {
        return this._rangeDefault ?? this.getPropertyDefinitions().rangeDefault.defaultValue;
    }

    public set rangeDefault(value: boolean) {
        this._rangeDefault = value;
    }

    public get isPresentingRange(): boolean {
        return this.switchRange?.checked === true;
    }

    private syncSwitchRange() {
        if (this.switchRange == null && this.rangeOffered === true && this.innerComponentsIdentified === true)
            this.switchRange = new Switch();
        else
            this.switchRange = null;
    }

    public get switchRange(): Switch {
        return this._switchRange;
    }

    private set switchRange(value: Switch) {
        const overrideProps: Partial<SwitchProps> = {
            id: this.createInnerComponentId(InnerIds.SWITCH_RANGE),
            caption: "Range Selection"
        };
        this.setInternalComponent({
            value,
            propName: "_switchRange",
            getPropertyDefinitionsOverride: null,
            defaultProps: null,
            overrideProps
        }, true);
        this.switchRange?.addChangeListener(this.syncTextboxes);
    }

    public resetSwitchRange() {
        if (this._designer == null && this.switchRange != null) {
            this.switchRange.checked = this.rangeDefault;
        }
    }

    private syncTextboxes = () => {
        this.createRangeTextboxes();
        if (this.isPresentingRange !== true) {
            this.textboxRangeEnd?.clear();
            this.textboxRangeEnd?.resetValidation();
            this.textboxRangeStart?.clear();
            this.textboxRangeStart?.resetValidation();
            this.addInternalComponent(this.textbox);
        }
        else {
            this.textbox.clear();
            this.textbox.resetValidation();
            this.addInternalComponent(this.textboxRangeEnd);
            this.addInternalComponent(this.textboxRangeStart);
        }
        this.checkbox.enabled = false;
        this.checkbox.checked = false;
    };

    private createRangeTextboxes() {
        if (this.textbox == null)
            this.textbox = new Textbox();
        if (this.switchRange?.checked === true) {
            if (this.dashLabel == null)
                this.dashLabel = new Label({ text: "-", rowBreak: false, marginTop: 12 });
            this.textbox.clear();
            this.textbox.resetValidation();
            if (this.textboxRangeEnd == null) {
                const cloneProps = { component: this.textbox, overrideProps: this.textboxRequiredProps };
                this.textboxRangeEnd = CloneComponent.clone(cloneProps) as Textbox;
            }
            if (this.textboxRangeStart == null) {
                const cloneProps = { component: this.textbox, overrideProps: this.textboxRequiredProps };
                this.textboxRangeStart = CloneComponent.clone(cloneProps) as Textbox;
            }
        }
    }

    /**
     * This method is called when one of the textboxes is changed in the designer (for example, if its lookup model
     * info is setup, etc.).  Because all of the textboxes should have the same props, apply the properties from that
     * textbox to the others.
     *
     * This really shouldn't be necessary, because we don't create/display the range start/end textboxes in the
     * designer.  But future-proof this in case we end up in a scenario where we do display them.
     * @param textbox The textbox that was changed in the designer
     */
    syncTextboxProps(textbox: Textbox, propName: string, _oldValue: any, newValue: any) {
        this.getOtherTextboxes(textbox).forEach(component => component[propName] = newValue);
    }

    private getOtherTextboxes(textbox: Textbox): Textbox[] {
        const result: Textbox[] = [ this.textbox ];
        if (this.textboxRangeEnd != null)
            result.push(this.textboxRangeEnd);
        if (this.textboxRangeStart != null)
            result.push(this.textboxRangeStart);
        return result.filter(component => component !== textbox);
    }

    private get lastTextbox(): Textbox {
        let result: Textbox;
        this.components?.forEach(component => {
            if (component instanceof Textbox)
                result = component;
        });
        return result;
    }

    public get textbox(): Textbox {
        return this._textbox;
    }

    private set textbox(value: Textbox) {
        const overrideProps: Partial<TextboxProps> = {
            ...this.commonTextboxProps,
            id: this.createInnerComponentId(InnerIds.TEXTBOX),
            field: this.field
        };
        this.setInternalComponent({
            value,
            propName: "_textbox",
            getPropertyDefinitionsOverride: null,
            defaultProps: null,
            overrideProps
        });
    }

    public get textboxRangeEnd(): Textbox {
        return this._textboxRangeEnd;
    }

    private set textboxRangeEnd(value: Textbox) {
        const overrideProps: Partial<TextboxProps> = {
            ...this.commonTextboxProps,
            id: this.createInnerComponentId(InnerIds.TEXTBOX_RANGE_END),
            caption: null,
            field: this.rangeEndFieldName,
            allowDropdownMultiSelect: false,
            lookupModelAllowMultiSelect: false,
            placeholder: " "
        };
        this.setInternalComponent({
            value,
            propName: "_textboxRangeEnd",
            getPropertyDefinitionsOverride: null,
            defaultProps: null,
            overrideProps
        }, true);
        this.textboxRangeEnd?.addChangeListener(this.checkRangeBoxContents);
        this.textboxRangeEnd?.addBlurListener(this.checkRangeBoxContents);
    }

    public get textboxRangeStart(): Textbox {
        return this._textboxRangeStart;
    }

    private set textboxRangeStart(value: Textbox) {
        const overrideProps: Partial<TextboxProps> = {
            ...this.commonTextboxProps,
            id: this.createInnerComponentId(InnerIds.TEXTBOX_RANGE_START),
            field: this.rangeStartFieldName,
            allowDropdownMultiSelect: false,
            lookupModelAllowMultiSelect: false,
            rowBreak: false,
            placeholder: " "
        };
        this.setInternalComponent({
            value,
            propName: "_textboxRangeStart",
            getPropertyDefinitionsOverride: null,
            defaultProps: null,
            overrideProps
        }, true);
        this.textboxRangeStart?.addChangeListener(this.checkRangeBoxContents);
        this.textboxRangeStart?.addBlurListener(this.checkRangeBoxContents);
    }

    private get commonTextboxProps(): Partial<TextboxProps> {
        return {
            dataSource: this.dataSource,
            searchOnly: this.searchOnly,
            fillRow: true,
            ...this.textboxRequiredProps
        };
    }

    private get textboxRequiredProps(): Partial<TextboxProps> {
        // Can't set inner textbox's required values using public required* property getters.
        // Doing so would result in inner textbox's _required value being set, which throws off its logic.
        return {
            required: this["_required"],                         // UGH
            requiredDuringAdd: this["_requiredDuringAdd"],       // UGH
            requiredDuringSearch: this["_requiredDuringSearch"], // UGH
            requiredDuringUpdate: this["_requiredDuringUpdate"], // UGH
        }
    }

    protected override addInternalComponent(value: Component) {
        if (value == null)
            return;
        if (value === this.switchRange) {
            if (!this.contains(this.switchRange))
                this.insert(this.switchRange, 0);
        }
        else if (value === this.checkbox)
            this.addIfNotPresent(value);
        else if (value === this.textbox || this.textboxRangeStart || this.textboxRangeEnd) {
            // Re-layout the available textboxes
            if (!this.contains(value)) {
                this.remove(this.textbox);
                this.remove(this.textboxRangeStart);
                this.remove(this.dashLabel);
                this.remove(this.textboxRangeEnd);
                const indexOfCheckbox = this.indexOf(this.checkbox);
                if (!this.isPresentingRange) {
                    if (this.textbox != null) {
                        if (indexOfCheckbox >= 0)
                            this.insert(this.textbox, indexOfCheckbox);
                        else
                            this.add(this.textbox);
                    }
                }
                else {
                    if (indexOfCheckbox >= 0) {
                        if (this.textboxRangeEnd != null)
                            this.insert(this.textboxRangeEnd, indexOfCheckbox);
                        if (this.dashLabel != null)
                            this.insert(this.dashLabel, indexOfCheckbox);
                        if (this.textboxRangeStart != null)
                            this.insert(this.textboxRangeStart, indexOfCheckbox);
                    }
                    else {
                        if (this.textboxRangeStart != null)
                            this.add(this.textboxRangeStart);
                        if (this.dashLabel != null)
                            this.add(this.dashLabel);
                        if (this.textboxRangeEnd != null)
                            this.add(this.textboxRangeEnd);
                    }
                }
            }
        }
    }

    protected override doAfterSetInternalComponent(component: Component): void {
        if (component === this.switchRange) {
            this.resetSwitchRange();
        }
        this.syncOrientation();
        this.syncCheckboxEventListeners(this.textbox);
        this.syncCheckboxEventListeners(this.textboxRangeEnd);
        this.syncCheckboxEventListeners(this.textboxRangeStart);
    }

    private syncCheckboxEventListeners(textbox: Textbox) {
        if (textbox != null) {
            textbox.addDataDisplayListener(this.updateCheckboxAvailability);
            textbox.removeLookupModelSelectionListener(this.updateCheckboxAvailability);
            // this.textbox.removeAfterDropdownSelectionListener(this.updateCheckboxAvailability);
            textbox.removeChangeListener(this.updateCheckboxAvailability);

            if (textbox.hasLookupModel() === true)
                textbox.addLookupModelSelectionListener(this.updateCheckboxAvailability);
            // else if (this.textbox.items != null)
            //     this.textbox.addAfterDropdownSelectionListener(this.updateCheckboxAvailability);
            else
                textbox.addChangeListener(this.updateCheckboxAvailability);
        }
    }

    private updateCheckboxAvailability = () => {
        if (this.checkbox == null)
            return;
        if (!this.isPresentingRange) {
            if (this.textbox == null)
                return;
            const textboxEmptyOrWildCard = this.textbox.isEmpty() === true || this.textbox.text.trim() === "*";
            if (textboxEmptyOrWildCard)
                this.checkbox.checked = false;
            this.checkbox.enabled = !textboxEmptyOrWildCard;
        }
        else {
            if (this.textboxRangeEnd == null || this.textboxRangeStart == null)
                return;
            const textboxRangeEndEmpty = this.textboxRangeEnd.isEmpty() === true;
            const textboxRangeStartEmpty = this.textboxRangeStart.isEmpty() === true;
            if (textboxRangeEndEmpty && textboxRangeStartEmpty)
                this.checkbox.checked = false;
            this.checkbox.enabled = !textboxRangeEndEmpty || !textboxRangeStartEmpty;
        }
    }

    private checkRangeBoxContents = (event: Event) => {
        const changedTextbox = event.target as Textbox;
        const changedTextboxIsEmpty = changedTextbox.isEmpty();
        const otherTextbox = changedTextbox === this.textboxRangeStart ? this.textboxRangeEnd : this.textboxRangeStart;
        const otherTextboxIsEmpty = otherTextbox.isEmpty();
        if (changedTextboxIsEmpty === false) {
            if (otherTextboxIsEmpty === true)
                otherTextbox.resetValidation();
        }
        else {
            if (otherTextboxIsEmpty !== true)
                changedTextbox.resetValidation();
        }
    }

    override _applyEnabled(value: boolean): void {
        if (this.switchRange != null)
            this.switchRange.enabled = value;
        this.checkbox.enabled = value;
        this.textbox.enabled = value;
        if (this.textboxRangeEnd != null)
            this.textboxRangeEnd.enabled = value;
        if (this.textboxRangeStart != null)
            this.textboxRangeStart.enabled = value;
        if (value)
            this._element.removeAttribute("disabled");
        else
            this._element.setAttribute("disabled", "true");
    }

    protected override _initialDropInDesigner() {
        super._initialDropInDesigner();
        this.syncSwitchRange();
        this.checkbox = new Checkbox();
        this.syncTextboxes();
    }

    /**
     * - Identify the inner switch, checkbox, and textbox that are created during deserialization/cloning.
     * - Create the range textboxes (if necessary).
     * - Also set the checkbox's availability.
     */
    protected override identifyInnerComponents() {
        this.components.forEach((component: Component) => {
            if (component instanceof Checkbox)
                this.checkbox = component;
            else if (component instanceof Switch)
                this.switchRange = component;
            else if (component instanceof Textbox)
                this.textbox = component;
        });
        this.syncTextboxes();
        this.updateCheckboxAvailability();
    }

    /**
     * The normal textbox may still be bound to the DataSource (since we don't destroy it when showing the range).  So
     * when validating, filter out the validation results for components that the user isn't viewing/using.
     *
     * Also, remove any duplicate 'required' validation results (which I think are a product of this component, which is
     * a panel, being bound to the dataSource).
     *
     * Finally, consolidate 'required field is missing' warnings for the range start/end fields into a single warning.
     * We only need one error when both fields are blank.
     *
     * @param event The DataSourceValidationEvent
     */
    private dataSourceOnValidate = (event: DataSourceValidationEvent) => {
        let rangeStartRequiredResult: ValidationResult;
        let rangeEndRequiredResult: ValidationResult;
        for (let x=event.validationResults.length-1 ; x>= 0 ; x--) {
            let removeResult = true;
            const validationResult = event.validationResults[x];
            if (validationResult.component !== this.textbox &&
                validationResult.component !== this.textboxRangeStart &&
                validationResult.component !== this.textboxRangeEnd) {
                removeResult = false;
            }
            else if (!this.isPresentingRange && validationResult.component === this.textbox) {
                removeResult = false;
            }
            else if (this.isPresentingRange && validationResult.component === this.textboxRangeStart) {
                if (rangeStartRequiredResult == null) {
                    rangeStartRequiredResult = validationResult;
                    removeResult = false;
                }
            }
            else if (this.isPresentingRange && validationResult.component === this.textboxRangeEnd) {
                if (rangeEndRequiredResult == null) {
                    rangeEndRequiredResult = validationResult;
                    removeResult = false;
                }
            }
            // Validation result is for a component that we don't want to validate (it's not being shown).  Remove the
            // validation result from the array.
            if (removeResult === true) {
                event.validationResults.splice(x, 1);
                if (validationResult.component instanceof Textbox)
                    validationResult.component.validationWarning = null;
            }
        }

        if (this.isPresentingRange) {
            const bothRangeValuesMissing =
                rangeStartRequiredResult?.isValid === false &&
                rangeStartRequiredResult?.type === ValidationResultType.REQUIRED &&
                rangeEndRequiredResult?.isValid === false &&
                rangeEndRequiredResult?.type === ValidationResultType.REQUIRED;
            if (bothRangeValuesMissing === true) {
                // Leave both boxes in error, but only show one validation result to the user
                this.removeTextboxValidationResult(rangeEndRequiredResult, event.validationResults);
                rangeStartRequiredResult.caption =
                    `The ${this.textboxRangeStart.caption} range start and/or end value must be provided.`;
            }
            else {
                if (rangeStartRequiredResult?.isValid === false) {
                    // Remove error from textbox, and remove the validation result from the array
                    this.removeTextboxValidationResult(rangeStartRequiredResult, event.validationResults);
                }
                if (rangeEndRequiredResult?.isValid === false) {
                    // Remove error from textbox, and remove the validation result from the array
                    this.removeTextboxValidationResult(rangeEndRequiredResult, event.validationResults);
                }
            }
        }
    }

    private removeTextboxValidationResult(result: ValidationResult, allResults: ValidationResult[]) {
        ArrayUtil.removeFromArray(allResults, result);
        if (result.component instanceof Textbox) {
            const otherRangeEndResult = allResults.find(validationResult => {
                validationResult.component === result.component;
            });
            if (otherRangeEndResult != null)
                result.component.validationWarning = otherRangeEndResult.validationMessage;
        }
    }

    /**
     * Override to control what parts of the component are serialized, see class description above.  We shouldn't end up
     * in a scenario where we display the range start/end boxes in the designer, but if we do we don't want to serialize
     * them.
     */
    _serializeNonProps(): string {
        const filteredComponents = [ ...this.components ];
        ArrayUtil.removeFromArray(filteredComponents, this.dashLabel);
        ArrayUtil.removeFromArray(filteredComponents, this.textboxRangeEnd);
        ArrayUtil.removeFromArray(filteredComponents, this.textboxRangeStart);
        if (!filteredComponents.includes(this.textbox)) {
            const indexOfCheckbox = filteredComponents.indexOf(this.checkbox);
            filteredComponents.splice(indexOfCheckbox, 0, this.textbox);
        }
        return "\"components\": " + serializeComponents(filteredComponents, null,) + ",\n";
    }

    override get dataSource(): DataSource {
        return super.dataSource;
    }

    override set dataSource(value: DataSource) {
        super.dataSource = value;
        if (this.dataSource != null)
            this.dataSource.addValidationListener(this.dataSourceOnValidate)
        else
            this.dataSource.removeValidationListener(this.dataSourceOnValidate);
        const props: Partial<ComponentProps> = { dataSource: value };
        this.setInnerComponentProps(this.checkbox, props);
        this.setInnerComponentProps(this.textbox, props);
        this.setInnerComponentProps(this.textboxRangeEnd, props);
        this.setInnerComponentProps(this.textboxRangeStart, props);
    }

    override get field(): string {
        return super.field;
    }

    override set field(value: string) {
        super.field = value;
        this.setInnerComponentProps(this.checkbox, { field: this.excludeFieldName });
        this.setInnerComponentProps(this.textbox, { field: value });
        this.setInnerComponentProps(this.textboxRangeEnd, { field: this.rangeEndFieldName });
        this.setInnerComponentProps(this.textboxRangeStart, { field: this.rangeStartFieldName });
    }

    private get excludeFieldName(): string {
        return this.field + "_exclude";
    }

    private get rangeStartFieldName(): string {
        return this.field + "_start";
    }

    private get rangeEndFieldName(): string {
        return this.field + "_end";
    }

    override get searchOnly(): boolean {
        return super.searchOnly;
    }

    override set searchOnly(value: boolean) {
        super.searchOnly = value;
        const props: Partial<ComponentProps> = { searchOnly: value };
        this.setInnerComponentProps(this.checkbox, props);
        this.setInnerComponentProps(this.switchRange, props);
        this.setInnerComponentProps(this.textbox, props);
        this.setInnerComponentProps(this.textboxRangeEnd, props);
        this.setInnerComponentProps(this.textboxRangeStart, props);
    }

    override get required(): boolean {
        return super.required;
    }

    override set required(value: boolean) {
        super.required = value;
        const props: Partial<ComponentProps> = { required: value };
        if (this.searchOnly === true)
            props.requiredDuringSearch = value;
        this.setInnerComponentProps(this.textbox, props);
        this.setInnerComponentProps(this.textboxRangeEnd, props);
        this.setInnerComponentProps(this.textboxRangeStart, props);
    }

    override get requiredDuringAdd(): boolean {
        return super.requiredDuringAdd;
    }

    override set requiredDuringAdd(value: boolean) {
        super.requiredDuringAdd = value;
        const props: Partial<ComponentProps> = { requiredDuringAdd: value };
        this.setInnerComponentProps(this.textbox, props);
        this.setInnerComponentProps(this.textboxRangeEnd, props);
        this.setInnerComponentProps(this.textboxRangeStart, props);
    }

    override get requiredDuringSearch(): boolean {
        return super.requiredDuringSearch;
    }

    override set requiredDuringSearch(value: boolean) {
        super.requiredDuringSearch = value;
        const props: Partial<ComponentProps> = { requiredDuringSearch: value };
        this.setInnerComponentProps(this.textbox, props);
        this.setInnerComponentProps(this.textboxRangeEnd, props);
        this.setInnerComponentProps(this.textboxRangeStart, props);
    }

    override get requiredDuringUpdate(): boolean {
        return super.requiredDuringUpdate;
    }

    override set requiredDuringUpdate(value: boolean) {
        super.requiredDuringUpdate = value;
        const props: Partial<ComponentProps> = { requiredDuringUpdate: value };
        this.setInnerComponentProps(this.textbox, props);
        this.setInnerComponentProps(this.textboxRangeEnd, props);
        this.setInnerComponentProps(this.textboxRangeStart, props);
    }

    override getPropertyDefinitions(): ComponentPropDefinitions {
        return ExcludableTextboxPropDefinitions.getDefinitions();
    }

    override get serializationName() {
        return "excludabletextbox";
    }

    override get properName(): string {
        return "Excludable Textbox";
    }

    // Do not display data for the range switch.  That component is part of the panel, but isn't bound to the
    // dataSource, so if we did call displayData() on it, it would always clear (uncheck) the switch.
    override getInnerComponentsForDisplay(): Component[] {
        return this.components.filter(component => component !== this.switchRange);
    }

    override resetNonDataState() {
        this.resetSwitchRange();
    }
}
ComponentTypes.registerComponentType("excludabletextbox", { constructor: ExcludableTextbox.prototype.constructor });
